import { useEffect, useState } from 'react';
import { signedIn } from '../../data/session';
import { api } from '../../lib/api';
import { QrCode } from '../../lib/qr';
import { navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import s from './Auth.module.css';
import { AuthLayout } from './AuthLayout';

interface StartResponse {
  setupId: string;
  passkeyOptions: unknown;
  totpSecret: string;
  totpUri: string;
}

/** First run: one passkey for this computer, plus a password and authenticator code as backup. */
export function SetupScreen() {
  const token =
    new URLSearchParams(location.hash.slice(1)).get('token') ??
    new URLSearchParams(location.search).get('token') ??
    '';
  const [start, setStart] = useState<StartResponse | null>(null);
  const [passkey, setPasskey] = useState<unknown>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    api<StartResponse>('/api/auth/setup/start', { body: { token } })
      .then(setStart)
      .catch((e) => setError((e as Error).message));
  }, [token]);

  if (!token) {
    return (
      <AuthLayout title="Set up">
        <p className="muted">
          Open the setup link from your instructions on this computer to finish setting up.
        </p>
      </AuthLayout>
    );
  }

  const makePasskey = async () => {
    if (!start) return;
    setError(null);
    try {
      const { startRegistration } = await import('@simplewebauthn/browser');
      const res = await startRegistration({
        optionsJSON: start.passkeyOptions as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      setPasskey(res);
    } catch (e) {
      setError(
        /NotAllowed|cancel|abort/i.test((e as Error).message)
          ? 'The passkey was cancelled. You can try again or skip it.'
          : (e as Error).message,
      );
    }
  };

  const finish = async () => {
    if (!start) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/setup/finish', {
        body: {
          setupId: start.setupId,
          token,
          password,
          totpCode: code,
          passkey: passkey ?? undefined,
        },
      });
      history.replaceState(null, '', '/');
      await signedIn();
      navigate('/', { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Set up your app">
      {!start ? (
        <p className={error ? s.error : 'muted'}>{error ?? 'Checking your setup link'}</p>
      ) : (
        <form
          className={s.steps}
          onSubmit={(e) => {
            e.preventDefault();
            void finish();
          }}
        >
          <p className="muted">1. Create a passkey so this computer signs in with Windows Hello.</p>
          <Button
            variant={passkey ? 'quiet' : 'primary'}
            icon={passkey ? 'check' : 'fingerprint'}
            onClick={() => void makePasskey()}
            disabled={!!passkey}
          >
            {passkey ? 'Passkey ready' : 'Create passkey'}
          </Button>
          <p className="muted">2. Choose a backup password of at least 12 characters.</p>
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
          />
          <p className="muted">
            3. Scan this in your authenticator app, then enter the 6-digit code it shows.
          </p>
          <div className={s.qr}>
            <QrCode text={start.totpUri} size={180} label="Authenticator setup code" />
          </div>
          <p className={s.secret}>{start.totpSecret}</p>
          <TextField
            label="Code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
            className="num"
          />
          {error && <p className={`${s.error} t-footnote`}>{error}</p>}
          <Button
            variant="primary"
            type="submit"
            wide
            disabled={busy || password.length < 12 || code.length !== 6}
          >
            Finish setup
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
